/**
 * Team leader in-app manual. Keep in sync with docs/manuals/TEAM_LEADER_MANUAL.md
 */

import { createHelpAssistantManualSection } from "./helpAssistantManualSection.js";

export const TEAM_LEADER_MANUAL_INTRO = {
  title: "Team Leader guide",
  subtitle:
    "Team Leaders oversee agents, locations, scripts, call quality, and reporting. Use the team-leader sidebar for management workflows.",
  defaultRoute: "/team-leader",
};

export const TEAM_LEADER_MANUAL_SECTIONS = [
  {
    id: "dashboard",
    group: "menu",
    menuLabel: "Dashboard",
    route: "/team-leader",
    keywords: ["home", "leader", "overview"],
    summary: "Team-level overview of agent activity and outcomes.",
    howToUse: [
      "Open **Dashboard** (`/team-leader`).",
      "Check team metrics and follow up on low-performing areas.",
    ],
  },
  {
    id: "my-locations",
    group: "menu",
    menuLabel: "My Locations",
    route: "/team-leader/locations",
    keywords: ["locations", "sites", "assignment"],
    summary: "Locations assigned to your leadership scope.",
    howToUse: [
      "Open **My Locations** (`/team-leader/locations`).",
      "Validate location coverage and agent distribution.",
    ],
  },
  {
    id: "my-agents",
    group: "menu",
    menuLabel: "My Agents",
    route: "/team-leader/agents",
    keywords: ["agents", "team", "coaching", "status", "assign"],
    summary:
      "Agent roster: view assigned agents, status, and performance; take coaching actions.",
    howToUse: [
      "Open **My Agents** (`/team-leader/agents`).",
      "Review agent activity; open an agent for detail and coaching notes.",
      "Coordinate assignments with [[My Locations|/team-leader/locations]].",
    ],
    faqs: [
      {
        question: "How do I review agent performance?",
        answer:
          "[[My Agents|/team-leader/agents]] for roster, [[Call History|/team-leader/call-history]] for calls, and [[Report|/reports]] for metrics.",
      },
    ],
  },
  {
    id: "scripts",
    group: "menu",
    menuLabel: "Scripts",
    route: "/team-leader/scripts",
    keywords: ["scripts", "talk tracks", "playbook", "create", "edit"],
    summary:
      "Call scripts for agents: create talk tracks aligned to campaigns and recare/reactivation goals.",
    howToUse: [
      "Open **Scripts** (`/team-leader/scripts`).",
      "Create or edit scripts; assign to locations or campaigns as the page allows.",
      "Agents see scripts during [[Lead Queue|/agent/lead-queue]] calls.",
    ],
    faqs: [
      {
        question: "How do I create a call script?",
        answer:
          "[[Scripts|/team-leader/scripts]] → Add Script → enter talk track content → save. Agents use scripts from Lead Queue.",
      },
    ],
  },
  {
    id: "call-history",
    group: "menu",
    menuLabel: "Call History",
    route: "/team-leader/call-history",
    keywords: ["calls", "quality", "history"],
    summary: "Team call log for review and quality monitoring.",
    howToUse: [
      "Open **Call History** (`/team-leader/call-history`).",
      "Filter by agent/date and review outcomes for coaching.",
    ],
  },
  {
    id: "report",
    group: "menu",
    menuLabel: "Report",
    route: "/reports",
    keywords: ["report", "analytics", "kpi"],
    summary: "Reporting page shared with agents and super admins.",
    howToUse: [
      "Open **Report** (`/reports`).",
      "Use date and grouping filters to evaluate team performance.",
    ],
  },
  {
    id: "my-templates",
    group: "more",
    menuLabel: "My Templates",
    route: "/agent/my-templates",
    keywords: ["templates", "sms", "messages"],
    summary:
      "Template manager route shared with agent/front desk permissions.",
    howToUse: [
      "Open `/agent/my-templates` when script/message assets need updates.",
      "Maintain reusable content for your team.",
    ],
  },
  {
    id: "profile",
    group: "account",
    menuLabel: "Your Profile",
    route: "/settings/profile",
    keywords: ["profile", "password", "account"],
    summary: "Personal account settings.",
    howToUse: [
      "Use avatar menu → **Your Profile** (`/settings/profile`).",
      "Update credentials and personal details.",
    ],
  },
  createHelpAssistantManualSection(),
];

export const TEAM_LEADER_MANUAL_GROUPS = {
  menu: { label: "Main menu" },
  account: { label: "Account" },
  more: { label: "More features" },
};

export function filterTeamLeaderManualSections(query, groupFilter) {
  const q = (query || "").trim().toLowerCase();
  return TEAM_LEADER_MANUAL_SECTIONS.filter((s) => {
    if (groupFilter && groupFilter !== "all" && s.group !== groupFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      s.menuLabel,
      s.summary,
      s.route,
      ...s.keywords,
      ...s.howToUse.join(" "),
      ...(s.faqs || []).map((f) => `${f.question} ${f.answer}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
